import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AbstractDynamicComponent } from '@components/abstracts';
import { AgendaMagasinTunnel } from '@components/agenda-magasin-tunnel/models';
import { TranslatePipe } from '@ngx-translate/core';
import { LoaderComponent } from '@shared/components/loader';
import { NoSlotFallbackCardComponent } from '@shared/components/no-slot-fallback-card';
import { SvgComponent } from '@shared/components/svg';
import { TitleComponent } from '@shared/components/title';
import { ReplaceWholeWordPipe, TransformFirstLetterPipe } from '@shared/pipe';
import { LogsService } from '@shared/services/logs';
import { TimeslotsService } from '@shared/services/timeslots';
import { FormattedSlots, Timeslots } from '@shared/services/timeslots/models';
import { CommonStore, FunnelStore } from '@stores';
import { EMPTY, take } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

@Component({
  selector: 'app-agenda-magasin-tunnel',
  imports: [
    TitleComponent,
    SvgComponent,
    NgClass,
    TransformFirstLetterPipe,
    ReplaceWholeWordPipe,
    LoaderComponent,
    TranslatePipe,
    NoSlotFallbackCardComponent,
  ],
  templateUrl: './agenda-magasin-tunnel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgendaMagasinTunnelComponent extends AbstractDynamicComponent<AgendaMagasinTunnel> implements OnInit {
  readonly visioAppointmentSelected = signal(false);
  readonly timeslots = signal<Timeslots[]>(null!);
  readonly isTimeslotsError = signal(false);
  readonly slotClosed = signal<Set<string>>(new Set());
  private readonly commonStore = inject(CommonStore);
  readonly appointmentFunnel = this.commonStore.select('appointmentFunnel');
  private readonly baseUrl = this.commonStore.select('baseUrl');
  private readonly funnelStore = inject(FunnelStore);
  readonly storeSelected = this.funnelStore.select('storeSelected');
  readonly slotSelected = this.funnelStore.select('slotSelected');
  private readonly router = inject(Router);
  private readonly timeslotsService = inject(TimeslotsService);
  private readonly logs = inject(LogsService);

  constructor() {
    super();
    effect(() => {
      const timeSlot = this.timeslots();
      if (timeSlot) {
        const slotClosed = this.slotClosed();
        timeSlot.forEach((slot) => {
          if (slot.formattedSlots.length === 0) {
            slotClosed.add(slot.dateLabel);
          }
        });
        this.slotClosed.set(slotClosed);
      }
    });
  }

  ngOnInit(): void {
    this.funnelStore.update('currentStep', 2);
    const storeSelected = this.storeSelected();
    if (!storeSelected) {
      this.logs.warn('AgendaMagasinTunnelComponent', 'No store has been selected. Return to step 1');
      if (this.appointmentFunnel()?.urls) {
        const redirectUrl = this.appointmentFunnel()?.urls.step1 ?? '';
        void this.router.navigate([redirectUrl]);
      } else {
        void this.router.navigate([this.baseUrl() ?? '']);
      }
    } else {
      const slotSelected = this.slotSelected();
      if (slotSelected?.visioAppointmentSelected && storeSelected.isVisioAppointmentAvailable) {
        this.visioAppointmentSelected.set(slotSelected.visioAppointmentSelected);
      }
      this.timeslotsService
        .getTimeslots(storeSelected.uuid)
        .pipe(
          take(1),
          tap((value) => {
            this.logs.debug('AgendaMagasinTunnelComponent', 'Successfully fetched timeslots', value);
            if ('title' in value) {
              this.isTimeslotsError.set(true);
            } else {
              const hasAvailableSlot = value.some((slot) => (slot.formattedSlots ?? []).length > 0);
              if (hasAvailableSlot) {
                this.logs.debug('AgendaMagasinTunnelComponent', 'Slots are available and will be displayed.');
                this.timeslots.set(value);
              } else {
                this.logs.debug('AgendaMagasinTunnelComponent', 'No available slots for the selected store.');
                this.isTimeslotsError.set(true);
              }
            }
          }),
          catchError((e) => {
            this.logs.error('AgendaMagasinTunnelComponent', 'An error occurred while requesting the timeslots', e);
            this.isTimeslotsError.set(true);
            return EMPTY;
          })
        )
        .subscribe();
    }
  }

  toggleSlot(dateLabel: string): void {
    const slotClosed = this.slotClosed();
    if (slotClosed.has(dateLabel)) {
      slotClosed.delete(dateLabel);
    } else {
      slotClosed.add(dateLabel);
    }
    this.slotClosed.set(slotClosed);
  }

  onSlotClick(date: Timeslots, formattedSlots: FormattedSlots): void {
    this.funnelStore.batchUpdate({
      update: {
        slotSelected: {
          dateLabel: date.dateLabel,
          formattedSlots,
          visioAppointmentSelected: this.visioAppointmentSelected(),
        },
        validatedStep: 2,
      },
    });
    const redirect = this.appointmentFunnel()?.urls.step3 ?? '';
    void this.router.navigate([redirect]);
  }
}
